class CedarwoodProductMedia extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.setupCarousel(signal);
    this.setupZoom(signal);
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.looping) this.disableLoop();
  }

  setupCarousel(signal) {
    this.list = this.querySelector('.c_p-product__media-list');
    this.dots = Array.from(this.querySelectorAll('.c_p-product__dot'));
    if (!this.list || this.dots.length === 0) return;
    this.items = Array.from(this.list.querySelectorAll('.c_p-product__media-item'));
    if (this.items.length === 0) return;
    this.onScroll = this.onScroll.bind(this);
    this.onLoopScroll = this.onLoopScroll.bind(this);
    this.list.addEventListener('scroll', this.onScroll, { passive: true, signal });
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToIndex(index), { signal });
    });

    this.loopSignal = signal;
    this.loopMedia = window.matchMedia('(max-width: 768px)');
    this.syncLoop = this.syncLoop.bind(this);
    this.loopMedia.addEventListener('change', this.syncLoop, { signal });
    this.syncLoop();
  }

  syncLoop() {
    const shouldLoop = this.loopMedia.matches && this.items.length > 1;
    if (shouldLoop && !this.looping) this.enableLoop();
    else if (!shouldLoop && this.looping) this.disableLoop();
  }

  enableLoop() {
    this.looping = true;
    const first = this.items[0];
    const last = this.items[this.items.length - 1];
    this.headClone = this.buildClone(last);
    this.tailClone = this.buildClone(first);
    this.list.insertBefore(this.headClone, first);
    this.list.appendChild(this.tailClone);
    this.jumpTo(first);
    requestAnimationFrame(() => {
      if (this.looping) this.jumpTo(this.items[0]);
    });
    this.list.addEventListener('scroll', this.onLoopScroll, { passive: true, signal: this.loopSignal });
  }

  buildClone(item) {
    const clone = item.cloneNode(true);
    clone.classList.add('is-clone');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.querySelectorAll('a, button, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
    // cloneNode を抜けた時点で deferred-media / product-model がアップグレードし、
    // ポスターに再生用の click が張られる。ポスターごと差し替えてリスナーを断つ（#250）
    clone.querySelectorAll('deferred-media, product-model').forEach((media) => {
      media.removeAttribute('loaded');
      media.querySelectorAll('video, iframe, model-viewer').forEach((el) => el.remove());
      const poster = media.querySelector('.deferred-media__poster');
      if (poster) poster.replaceWith(poster.cloneNode(true));
    });
    // 実スライドと id が重複し、getElementById が文書順で先にあるクローンを返してしまう
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    // Shopify の XR 起動ボタンは data 属性で登録されるため複製を残さない
    clone.querySelectorAll('[data-shopify-xr]').forEach((el) => el.remove());
    return clone;
  }

  disableLoop() {
    this.looping = false;
    this.list.removeEventListener('scroll', this.onLoopScroll);
    this.headClone?.remove();
    this.tailClone?.remove();
    this.headClone = null;
    this.tailClone = null;
  }

  onLoopScroll() {
    if (!this.looping) return;
    const settled = (el) => Math.abs(this.list.scrollLeft - this.snapLeft(el)) < 2;
    if (settled(this.headClone)) this.jumpTo(this.items[this.items.length - 1]);
    else if (settled(this.tailClone)) this.jumpTo(this.items[0]);
  }

  scrollToIndex(index) {
    const item = this.items[index];
    if (!item) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.list.scrollTo({ left: this.snapLeft(item), behavior: reduced ? 'auto' : 'smooth' });
  }

  jumpTo(item) {
    this.list.scrollTo({ left: this.snapLeft(item), behavior: 'auto' });
  }

  snapLeft(item) {
    const listRect = this.list.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    return this.list.scrollLeft + itemRect.left - listRect.left + itemRect.width / 2 - listRect.width / 2;
  }

  onScroll() {
    const children = this.looping ? [this.headClone].concat(this.items, this.tailClone) : this.items;
    const center = this.list.scrollLeft + this.list.clientWidth / 2;
    let idx = 0;
    for (let i = 0; i < children.length; i += 1) {
      if (children[i].offsetLeft <= center) idx = i;
    }
    let index = idx;
    if (this.looping) {
      if (children[idx] === this.headClone) index = this.items.length - 1;
      else if (children[idx] === this.tailClone) index = 0;
      else index = idx - 1;
    }
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  setupZoom(signal) {
    this.dialog = this.querySelector('.c_p-product__zoom');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.zoomImage = this.dialog.querySelector('[data-zoom-target]');
    this.triggers = Array.from(this.querySelectorAll('.c_p-product__media-trigger')).filter(
      (trigger) => !trigger.closest('.is-clone'),
    );
    if (!this.zoomImage || this.triggers.length === 0) return;
    this.zoomIndex = 0;
    this.preloaded = new Set();
    this.zoomCurrent = this.dialog.querySelector('[data-zoom-current]');
    this.zoomStatus = this.dialog.querySelector('[data-zoom-status]');

    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.openZoom(index), { signal });
    });

    const closeBtn = this.dialog.querySelector('[data-zoom-close]');
    const prevBtn = this.dialog.querySelector('[data-zoom-prev]');
    const nextBtn = this.dialog.querySelector('[data-zoom-next]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.dialog.close(), { signal });
    if (prevBtn) prevBtn.addEventListener('click', () => this.stepZoom(-1), { signal });
    if (nextBtn) nextBtn.addEventListener('click', () => this.stepZoom(1), { signal });

    this.dialog.addEventListener('click', this.onZoomClick.bind(this), { signal });
    this.dialog.addEventListener('keydown', this.onZoomKeydown.bind(this), { signal });
    this.dialog.addEventListener('close', this.onZoomClose.bind(this), { signal });
    this.dialog.addEventListener('pointerdown', this.onZoomPointerDown.bind(this), { signal });
    this.dialog.addEventListener('pointerup', this.onZoomPointerUp.bind(this), { signal });
    this.dialog.addEventListener(
      'pointercancel',
      () => {
        this.swipeStartX = null;
      },
      { signal },
    );
  }

  openZoom(index) {
    this.zoomIndex = index;
    this.renderZoom();
    this.dialog.showModal();
  }

  stepZoom(delta) {
    const total = this.triggers.length;
    this.zoomIndex = (this.zoomIndex + delta + total) % total;
    this.renderZoom();
  }

  renderZoom() {
    const trigger = this.triggers[this.zoomIndex];
    const src = trigger.dataset.zoomSrc;
    const position = String(this.zoomIndex + 1);
    if (this.zoomCurrent) this.zoomCurrent.textContent = position;
    if (this.zoomStatus) {
      this.zoomStatus.textContent = (this.dialog.dataset.zoomStatusTemplate || '').replace('%index%', position);
    }
    this.zoomImage.alt = trigger.dataset.zoomAlt || '';
    if (trigger.dataset.zoomWidth && trigger.dataset.zoomHeight) {
      this.zoomImage.width = Number(trigger.dataset.zoomWidth);
      this.zoomImage.height = Number(trigger.dataset.zoomHeight);
    }

    const hires = new Image();
    hires.src = src;
    if (hires.complete) {
      this.zoomImage.src = src;
    } else {
      const thumb = trigger.querySelector('img');
      this.zoomImage.src = (thumb && (thumb.currentSrc || thumb.src)) || src;
      hires.addEventListener(
        'load',
        () => {
          if (this.triggers[this.zoomIndex] === trigger) this.zoomImage.src = src;
        },
        { once: true },
      );
    }

    this.preloadZoomNeighbors();
  }

  preloadZoomNeighbors() {
    const total = this.triggers.length;
    if (total < 2) return;
    [-1, 1].forEach((offset) => {
      const src = this.triggers[(this.zoomIndex + offset + total) % total].dataset.zoomSrc;
      if (!src || this.preloaded.has(src)) return;
      this.preloaded.add(src);
      const img = new Image();
      img.src = src;
    });
  }

  onZoomClick(event) {
    if (this.swipeHandled) {
      this.swipeHandled = false;
      return;
    }
    if (event.target.closest('.c_p-product__zoom-nav, .c_p-product__zoom-close')) return;
    this.dialog.close();
  }

  onZoomKeydown(event) {
    if (event.key === 'ArrowLeft') this.stepZoom(-1);
    else if (event.key === 'ArrowRight') this.stepZoom(1);
  }

  onZoomPointerDown(event) {
    if (event.pointerType === 'mouse') return;
    this.swipeStartX = event.clientX;
  }

  onZoomPointerUp(event) {
    if (this.swipeStartX == null) return;
    const distance = event.clientX - this.swipeStartX;
    this.swipeStartX = null;
    if (Math.abs(distance) < 40) return;
    this.swipeHandled = true;
    if (distance < 0) this.stepZoom(1);
    else this.stepZoom(-1);
  }

  onZoomClose() {
    if (!this.items) return;
    const item = this.triggers[this.zoomIndex].closest('.c_p-product__media-item');
    const index = this.items.indexOf(item);
    if (index >= 0) this.scrollToIndex(index);
  }
}

if (!customElements.get('cedarwood-product-media')) {
  customElements.define('cedarwood-product-media', CedarwoodProductMedia);
}
